// ClientSignaturesTab.js
// Wrapper component for Client Signatures within the Misc tab
// Renders the existing ClientSignatureTable component

import React, { useState, useEffect } from "react";
import { SignatureModal } from "../../../common/SignatureModal";
import styles from "./ClientSignaturesTab.module.css";

// Reused components from clientSignatureComponents.js
const ClientSignatureNameInput = ({ disabled, name, setName }) => {
  return (
    <div className={styles.inputField}>
      <label>Name</label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={disabled}
        placeholder="Name"
        maxLength={300}
        className={styles.textInput}
      />
    </div>
  );
};

const ClientSignatureTitleInput = ({ disabled, title, setTitle }) => {
  return (
    <div className={styles.inputField}>
      <label>Title</label>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        disabled={disabled}
        placeholder="Title (optional)"
        maxLength={300}
        className={styles.textInput}
      />
    </div>
  );
};

const ClientSignatureCompanyInput = ({ disabled, company, setCompany }) => {
  return (
    <div className={styles.inputField}>
      <label>Company</label>
      <input
        type="text"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        disabled={disabled}
        placeholder="Company name"
        maxLength={250}
        className={styles.textInput}
      />
    </div>
  );
};

const ClientSignatureRow = ({ item, disabled, actions, emitPatch }) => {
  const onEdit = () => actions.openSignatureModal(item.rowId);

  const onDelete = async () => {
    actions.removeClientSignature(item.rowId, emitPatch);
  };

  return (
    <div className={styles.signatureCard} key={item.rowId}>
      <div className={styles.infoGrid}>
        <div className={styles.infoField}>
          <label className={styles.infoLabel}>Name</label>
          <div className={styles.infoValue}>
            {item.clientEmpName || (
              <span className={styles.emptyValue}>No name provided</span>
            )}
          </div>
        </div>
        <div className={styles.infoField}>
          <label className={styles.infoLabel}>Title</label>
          <div className={styles.infoValue}>
            {item.clientEmpTitle || (
              <span className={styles.emptyValue}>No title provided</span>
            )}
          </div>
        </div>
        <div className={styles.infoField}>
          <label className={styles.infoLabel}>Company</label>
          <div className={styles.infoValue}>
            {item.clientCompanyName || (
              <span className={styles.emptyValue}>No company provided</span>
            )}
          </div>
        </div>
      </div>

      <div
        className={styles.signatureDisplay}
        onClick={!disabled ? onEdit : undefined}
      >
        {item.clientEmpSig ? (
          <img
            src={item.clientEmpSig}
            alt="Client signature"
            className={styles.signatureImage}
          />
        ) : (
          <div className={styles.emptySignature}>
            <span>Click to sign</span>
          </div>
        )}
      </div>

      <div className={styles.signatureCardActions}>
        <button
          className={styles.signButton}
          onClick={onEdit}
          disabled={disabled}
        >
          Edit
        </button>
        <button
          className={styles.deleteButton}
          onClick={onDelete}
          disabled={disabled}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

/**
 * ClientSignaturesTab component
 * @param {object} callbacks - Contains delData, delMode, clientSignaturesUI, emitPatch, and signature actions
 */
export const ClientSignaturesTab = ({ callbacks }) => {
  const disabled = callbacks.delMode === "view";
  const ui = callbacks.clientSignaturesUI;

  // Handler to add new signature and immediately open modal
  const onAdd = () => {
    const newRowId = callbacks.addNewBlankClientSignature(callbacks.emitPatch);
    // Immediately open the modal for the newly created signature
    callbacks.openSignatureModal(newRowId);
  };

  // Finding the current signature row to pass to the modalComponents
  const currentSignatureRow = callbacks.delData?.clientSignatures?.find(
    (item) => item.rowId === ui.currentSignatureRowId
  );

  // Local state options for update by signature modal components
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");

  // Update local state when modal opens with a new signature
  useEffect(() => {
    if (ui.signatureModalOpen && currentSignatureRow) {
      setName(currentSignatureRow.clientEmpName || "");
      setTitle(currentSignatureRow.clientEmpTitle || "");
      setCompany(currentSignatureRow.clientCompanyName || "");
    }
  }, [ui.signatureModalOpen, ui.currentSignatureRowId, currentSignatureRow]);

  const handleClose = () => {
    // If this is a new signature that hasn't been saved, remove it
    const removeIfUnsaved = ui.isNewSignature;
    callbacks.closeSignatureModal(removeIfUnsaved, ui.currentSignatureRowId);
    // Reset local state on close
    setName("");
    setTitle("");
    setCompany("");
  };

  const handleSave = async (signature, signeeRowId) => {
    // First update the local state immediately for UI responsiveness
    callbacks.saveSignature(
      signature,
      signeeRowId,
      name,
      title,
      company,
      callbacks.emitPatch
    );

    // Reset local state after save
    setName("");
    setTitle("");
    setCompany("");
  };

  const modalComponents = [
    <ClientSignatureNameInput
      key="name"
      disabled={disabled}
      name={name}
      setName={setName}
    />,
    <ClientSignatureTitleInput
      key="title"
      disabled={disabled}
      title={title}
      setTitle={setTitle}
    />,
    <ClientSignatureCompanyInput
      key="company"
      disabled={disabled}
      company={company}
      setCompany={setCompany}
    />,
  ];

  return (
    <div className={styles.clientSignaturesTabContainer}>
      <div className={styles.header}>
        <h3 className={styles.headerTitle}>Client Signatures</h3>
      </div>

      <SignatureModal
        isOpen={ui.signatureModalOpen}
        onClose={handleClose}
        signee={ui.currentSignatureRowId}
        existingSignature={ui.currentSignatureImage}
        onSave={handleSave}
        onClear={() => {}}
        components={modalComponents}
      />

      <div className={styles.signatureCardList}>
        {(callbacks.delData?.clientSignatures || []).map((item) => (
          <ClientSignatureRow
            key={item.rowId}
            item={item}
            disabled={disabled}
            actions={callbacks}
            emitPatch={callbacks.emitPatch}
          />
        ))}
      </div>

      <div className={styles.signatureActionButtons}>
        <button
          className={styles.addButton}
          onClick={onAdd}
          disabled={disabled}
        >
          Add Client Signature
        </button>
      </div>
    </div>
  );
};

export default ClientSignaturesTab;